import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { assessmentService } from '../../api';
import { 
    Plus, ArrowLeft, HelpCircle, 
    Trash2, Edit2, CheckCircle, 
    XCircle, Save, X, GripVertical,
    Type, Users, Clock, ExternalLink,
    Search, Filter, ChevronRight, Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import ListPagination from '../../components/list/ListPagination';
import ListEmptyState from '../../components/list/ListEmptyState';
import StatusPill from '../../components/list/StatusPill';
import { DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';

function AdminAssessmentQuestions() {
    const { id } = useParams();
    const navigate = useNavigate();
    
    // Core States
    const [assessment, setAssessment] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    // Modal & UI States
    const [modalOpen, setModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [activeTab, setActiveTab] = useState('questions'); // legacy shows both, but we can offer tabs for focus
    const [subPage, setSubPage] = useState(1);
    const [subPageSize, setSubPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
    
    const [formData, setFormData] = useState({
        question_text: '',
        question_type: 'multiple_choice',
        points: 5,
        options: [
            { text: '', is_correct: true },
            { text: '', is_correct: false }
        ]
    });

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [assessRes, questionsRes, submissionsRes] = await Promise.all([
                assessmentService.getAssessmentDetail(id),
                assessmentService.getQuestions(id),
                assessmentService.getSubmissions({ assessment: id })
            ]);
            setAssessment(assessRes.data);
            setQuestions(questionsRes.data.results || questionsRes.data);
            setSubmissions(submissionsRes.data.results || submissionsRes.data);
        } catch (err) {
            toast.error('Failed to load assessment management data');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (q = null) => {
        if (q) {
            setEditingQuestion(q);
            setFormData({
                question_text: q.question_text,
                question_type: q.question_type,
                points: q.points,
                options: q.options && q.options.length > 0 ? q.options : [
                    { text: '', is_correct: true },
                    { text: '', is_correct: false }
                ]
            });
        } else {
            setEditingQuestion(null);
            setFormData({
                question_text: '',
                question_type: 'multiple_choice',
                points: 5,
                options: [
                    { text: '', is_correct: true },
                    { text: '', is_correct: false }
                ]
            });
        }
        setModalOpen(true);
    };

    const handleAddOption = () => {
        setFormData({
            ...formData,
            options: [...formData.options, { text: '', is_correct: false }]
        });
    };

    const handleRemoveOption = (index) => {
        const newOptions = formData.options.filter((_, i) => i !== index);
        if (formData.options[index].is_correct && newOptions.length > 0) {
            newOptions[0].is_correct = true;
        }
        setFormData({ ...formData, options: newOptions });
    };

    const handleOptionTextChange = (index, text) => {
        const newOptions = [...formData.options];
        newOptions[index].text = text;
        setFormData({ ...formData, options: newOptions });
    };

    const handleSetCorrect = (index) => {
        const newOptions = formData.options.map((opt, i) => ({
            ...opt,
            is_correct: i === index
        }));
        setFormData({ ...formData, options: newOptions });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                assessment: id
            };

            if (editingQuestion) {
                await assessmentService.updateQuestion(editingQuestion.id, payload);
                toast.success('Question updated successfully');
            } else {
                await assessmentService.createQuestion(payload);
                toast.success('Question added to assessment');
            }
            setModalOpen(false);
            fetchData();
        } catch (err) {
            toast.error('Failed to save question');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (qId) => {
        if (!window.confirm('Are you sure you want to delete this question?')) return;
        try {
            await assessmentService.deleteQuestion(qId);
            toast.success('Question removed');
            fetchData();
        } catch (err) {
            toast.error('Failed to delete question');
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex flex-col items-center justify-center h-[calc(100vh-80px)]">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            
            <main className="container max-w-6xl mx-auto px-4 py-8 md:py-12">
                {/* Modern Header */}
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                    <div className="flex items-center gap-6">
                        <button 
                            onClick={() => navigate('/admin/assessments')}
                            className="p-4 rounded-2xl bg-white border border-slate-100 text-slate-400 hover:text-slate-900 shadow-sm transition-all hover:border-primary/20 hover:bg-primary/5"
                        >
                            <ArrowLeft size={24} />
                        </button>
                        <div>
                            <div className="flex items-center gap-2 text-xs font-black text-primary uppercase tracking-[0.2em] mb-2">
                                <Plus size={14} />
                                {assessment?.assessment_type} Builder
                            </div>
                            <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight leading-none">{assessment?.title}</h1>
                            <p className="text-slate-500 mt-3 font-medium flex items-center gap-2">
                                <Users size={16} />
                                {assessment?.course_name} • {questions.length} Questions
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => openModal()}
                            className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 rounded-2xl font-bold shadow-xl shadow-slate-200 transition-all flex items-center gap-3 active:scale-95 whitespace-nowrap"
                        >
                            <Plus size={20} />
                            Add Question
                        </button>
                    </div>
                </header>

                <div className="space-y-16">
                    {/* Questions Section (Legacy Inspired Section) */}
                    <section>
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                                <HelpCircle className="text-primary" size={28} />
                                Questions ({questions.length})
                            </h2>
                        </div>

                        <div className="space-y-6">
                            {questions.length > 0 ? (
                                questions.map((q, idx) => (
                                    <motion.div
                                        key={q.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm group hover:shadow-2xl hover:shadow-slate-200 transition-all"
                                    >
                                        <div className="flex justify-between items-start gap-6">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-5">
                                                    <span className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-sm font-black text-slate-400">
                                                        Q{idx + 1}
                                                    </span>
                                                    <span className="px-4 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-[11px] font-black uppercase tracking-wider">
                                                        {q.points} Points
                                                    </span>
                                                    {q.question_type === 'short_answer' && (
                                                        <span className="px-4 py-1.5 rounded-lg bg-orange-50 text-orange-600 text-[11px] font-black uppercase tracking-wider">
                                                            Needs Grading
                                                        </span>
                                                    )}
                                                </div>
                                                <h3 className="text-xl font-bold text-slate-900 mb-8 leading-relaxed max-w-3xl">
                                                    {q.question_text}
                                                </h3>
                                                
                                                {q.question_type === 'multiple_choice' && (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                                        {q.options?.map((opt, i) => (
                                                            <div 
                                                                key={i} 
                                                                className={`p-5 rounded-2xl border-2 flex items-center justify-between gap-4 transition-all ${
                                                                    opt.is_correct 
                                                                    ? 'bg-emerald-50 border-emerald-100 text-emerald-900' 
                                                                    : 'bg-slate-50 border-slate-50 text-slate-500'
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-4">
                                                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                                                        opt.is_correct ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-200'
                                                                    }`}>
                                                                        {opt.is_correct && <CheckCircle size={14} />}
                                                                    </div>
                                                                    <span className="text-sm font-bold">{opt.text}</span>
                                                                </div>
                                                                {opt.is_correct && <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Correct</span>}
                                                            </div>
                                                        ))}
                                                        
                                                        {/* Quick Add Option (Legacy parity) */}
                                                        <button 
                                                            onClick={() => openModal(q)}
                                                            className="flex items-center justify-center gap-2 p-5 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-primary/40 hover:text-primary transition-all font-bold text-sm"
                                                        >
                                                            <Plus size={18} />
                                                            Add Option
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="flex flex-col gap-3">
                                                <button 
                                                    onClick={() => openModal(q)}
                                                    className="p-4 rounded-xl bg-slate-50 text-slate-400 hover:bg-primary hover:text-white transition-all shadow-sm group-hover:bg-slate-100"
                                                    title="Edit Question"
                                                >
                                                    <Edit2 size={20} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(q.id)}
                                                    className="p-4 rounded-xl bg-slate-50 text-slate-400 hover:bg-rose-500 hover:text-white transition-all shadow-sm group-hover:bg-slate-100"
                                                    title="Delete Question"
                                                >
                                                    <Trash2 size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            ) : (
                                <div className="bg-white rounded-[3rem] p-24 text-center border-2 border-dashed border-slate-100">
                                    <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-8">
                                        <HelpCircle size={48} className="text-slate-200" />
                                    </div>
                                    <h3 className="text-2xl font-black text-slate-800 tracking-tight">No questions added yet</h3>
                                    <p className="text-slate-500 mt-2 max-w-xs mx-auto font-medium">Click the button above to start building your assessment.</p>
                                </div>
                            )}

                            {questions.length > 0 && (
                                <div className="text-center pt-8">
                                    <button
                                        onClick={() => openModal()}
                                        className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-5 rounded-[2rem] font-black shadow-xl shadow-emerald-100 transition-all flex items-center gap-3 active:scale-95 mx-auto uppercase text-xs tracking-widest"
                                    >
                                        <Plus size={20} />
                                        Add Another Question
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Submissions Section (Directly below as in legacy) */}
                    <section className="pt-12 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                                <Users className="text-primary" size={28} />
                                Submissions ({submissions.length})
                            </h2>
                        </div>

                        <div className="ux-table-shell">
                            {submissions.length === 0 ? (
                                <ListEmptyState icon={Users} title="No submissions yet" description="Student submissions will appear here." />
                            ) : (
                                <>
                                    <DataRowList>
                                        {submissions
                                            .slice((subPage - 1) * subPageSize, subPage * subPageSize)
                                            .map((sub, index) => (
                                                <DataRow
                                                    key={sub.id}
                                                    index={index}
                                                    leading={(
                                                        <div className="ux-avatar">
                                                            {sub.student_name ? sub.student_name.charAt(0) : 'S'}
                                                        </div>
                                                    )}
                                                    title={sub.student_name || 'Anonymous Student'}
                                                    subtitle={`Attempt ${sub.attempt_number || 1}`}
                                                    meta={(
                                                        <>
                                                            <span><Clock size={12} /> {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : 'In progress'}</span>
                                                            <span>Score: {sub.score !== null ? `${parseFloat(sub.score).toFixed(2)}%` : '—'}</span>
                                                        </>
                                                    )}
                                                    badges={<StatusPill status={sub.status || 'pending'} label={sub.status || 'pending'} />}
                                                    actions={(
                                                        <Link
                                                            to={sub.status === 'pending' ? `/admin/submissions/${sub.id}/grade` : `/assessments/${sub.id}/result`}
                                                            className="ux-icon-btn"
                                                            title={sub.status === 'pending' ? 'Grade' : 'View result'}
                                                        >
                                                            <Eye size={16} />
                                                        </Link>
                                                    )}
                                                />
                                            ))}
                                    </DataRowList>
                                    <ListPagination
                                        page={subPage}
                                        pageSize={subPageSize}
                                        totalCount={submissions.length}
                                        onPageChange={setSubPage}
                                        onPageSizeChange={(size) => { setSubPageSize(size); setSubPage(1); }}
                                    />
                                </>
                            )}
                        </div>
                    </section>
                </div>

                {/* Question Editor Modal */}
                <AnimatePresence>
                    {modalOpen && (
                        <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
                            <motion.div 
                                initial={{ opacity: 0 }} 
                                animate={{ opacity: 1 }} 
                                exit={{ opacity: 0 }}
                                onClick={() => setModalOpen(false)}
                                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                            />
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                className="relative bg-white w-full max-w-3xl rounded-[3rem] shadow-2xl overflow-hidden overflow-y-auto max-h-[90vh]"
                                onClick={e => e.stopPropagation()}
                            >
                                <form onSubmit={handleSubmit}>
                                    <div className="p-8 md:p-12">
                                        <header className="flex items-center justify-between mb-10">
                                            <div>
                                                <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                                                    {editingQuestion ? 'Edit Question' : 'Add New Question'}
                                                </h2>
                                                <p className="text-slate-500 font-medium mt-1">Configure your evaluation content below.</p>
                                            </div>
                                            <button 
                                                type="button" 
                                                onClick={() => setModalOpen(false)}
                                                className="p-3 rounded-2xl hover:bg-slate-50 text-slate-400 transition-all"
                                            >
                                                <X size={28} />
                                            </button>
                                        </header>

                                        <div className="space-y-8">
                                            <div className="space-y-3">
                                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Question Content</label>
                                                <textarea
                                                    required
                                                    className="w-full px-8 py-6 rounded-[2rem] border-2 border-slate-50 bg-slate-50/50 focus:bg-white focus:border-primary/20 focus:ring-4 focus:ring-primary/5 outline-none transition-all font-bold text-slate-800 text-lg min-h-[140px] shadow-inner"
                                                    placeholder="Enter your question here..."
                                                    value={formData.question_text}
                                                    onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-3">
                                                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Question Type</label>
                                                    <div className="grid grid-cols-2 bg-slate-50/50 p-1.5 rounded-2xl border-2 border-slate-50">
                                                        <button
                                                            type="button"
                                                            onClick={() => setFormData({ ...formData, question_type: 'multiple_choice' })}
                                                            className={`py-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                                                                formData.question_type === 'multiple_choice' 
                                                                ? 'bg-white text-primary shadow-lg shadow-primary/5 border border-primary/10' 
                                                                : 'text-slate-400 hover:text-slate-600'
                                                            }`}
                                                        >
                                                            Multiple Choice
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setFormData({ ...formData, question_type: 'short_answer' })}
                                                            className={`py-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                                                                formData.question_type === 'short_answer' 
                                                                ? 'bg-white text-primary shadow-lg shadow-primary/5 border border-primary/10' 
                                                                : 'text-slate-400 hover:text-slate-600'
                                                            }`}
                                                        >
                                                            Short Answer
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="space-y-3">
                                                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Points Value</label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-8 py-4 rounded-2xl border-2 border-slate-50 bg-slate-50/50 focus:bg-white focus:border-primary/20 outline-none transition-all font-black text-slate-800"
                                                        value={formData.points}
                                                        onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
                                                    />
                                                </div>
                                            </div>

                                            {formData.question_type === 'multiple_choice' && (
                                                <div className="space-y-6 pt-6 border-t border-slate-100">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <h3 className="font-black text-slate-900 text-lg">Answer Options</h3>
                                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Select the correct choice</p>
                                                        </div>
                                                        <button 
                                                            type="button" 
                                                            onClick={handleAddOption}
                                                            className="flex items-center gap-2 text-primary font-black text-[11px] uppercase tracking-widest hover:bg-primary/5 px-4 py-2 rounded-xl transition-all"
                                                        >
                                                            <Plus size={16} />
                                                            Add Option
                                                        </button>
                                                    </div>
                                                    
                                                    <div className="space-y-4">
                                                        {formData.options.map((opt, i) => (
                                                            <div key={i} className="flex gap-3">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSetCorrect(i)}
                                                                    className={`w-16 rounded-[1.25rem] flex items-center justify-center transition-all border-2 ${
                                                                        opt.is_correct 
                                                                        ? 'bg-emerald-500 border-emerald-500 text-white shadow-xl shadow-emerald-200' 
                                                                        : 'bg-slate-50 border-slate-50 text-slate-300 hover:border-slate-200'
                                                                    }`}
                                                                >
                                                                    {opt.is_correct ? <CheckCircle size={24} /> : <div className="w-6 h-6 rounded-full border-4 border-slate-200" />}
                                                                </button>
                                                                <input
                                                                    required
                                                                    type="text"
                                                                    placeholder={`Option ${i + 1} content...`}
                                                                    className="flex-1 px-8 py-5 rounded-[1.5rem] border-2 border-slate-50 bg-slate-50/50 focus:bg-white focus:border-primary/20 outline-none transition-all font-bold text-slate-800"
                                                                    value={opt.text}
                                                                    onChange={(e) => handleOptionTextChange(i, e.target.value)}
                                                                />
                                                                {formData.options.length > 2 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveOption(i)}
                                                                        className="p-5 text-slate-300 hover:text-rose-500 transition-all"
                                                                    >
                                                                        <Trash2 size={22} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="p-10 border-t border-slate-100 flex items-center justify-end gap-5 bg-slate-50/20 backdrop-blur-md sticky bottom-0">
                                        <button
                                            type="button"
                                            onClick={() => setModalOpen(false)}
                                            className="px-8 py-4 rounded-2xl font-black text-slate-400 hover:text-slate-600 transition-all text-xs uppercase tracking-[0.2em]"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="bg-slate-900 hover:bg-slate-800 text-white px-10 py-5 rounded-2xl font-black shadow-2xl shadow-slate-200 transition-all active:scale-95 flex items-center gap-3 disabled:opacity-50 uppercase text-xs tracking-widest"
                                        >
                                            {submitting ? (
                                                <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                                            ) : (
                                                <Save size={20} />
                                            )}
                                            {editingQuestion ? 'Update Question' : 'Save Question'}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>
            </main>
        </DashboardLayout>
    );
}

export default AdminAssessmentQuestions;
